import { Component } from '@theme/component';

/**
 * A number input with plus and minus buttons.
 *
 * The `<input type="number">` stays the source of truth so the field is
 * typeable and the form still submits a quantity without JavaScript; the
 * buttons only step it and re-fire `change` so whatever is listening (a cart
 * line, a product form) reacts the same way it would to a typed value.
 *
 * @extends {Component}
 */
class QuantitySelectorComponent extends Component {
  requiredRefs = ['input'];

  /** @returns {HTMLInputElement} */
  get input() {
    return /** @type {HTMLInputElement} */ (this.refs.input);
  }

  increment = () => this.#step(1);
  decrement = () => this.#step(-1);

  /** @param {number} delta */
  #step(delta) {
    const min = Number(this.input.min || 0);
    const max = this.input.max ? Number(this.input.max) : Infinity;
    const next = Math.min(max, Math.max(min, Number(this.input.value || 0) + delta));

    if (next === Number(this.input.value)) return;

    this.input.value = String(next);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.refresh();
  }

  /** Greys out a button that can no longer do anything. */
  refresh = () => {
    const value = Number(this.input.value || 0);
    const min = Number(this.input.min || 0);
    const max = this.input.max ? Number(this.input.max) : Infinity;

    if (this.refs.decrement instanceof HTMLButtonElement) {
      this.refs.decrement.disabled = value <= min;
    }
    if (this.refs.increment instanceof HTMLButtonElement) {
      this.refs.increment.disabled = value >= max;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.refresh();
  }

  updatedCallback() {
    super.updatedCallback();
    this.refresh();
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
